"use client";

import * as React from "react";
import {
  useRouter,
} from "next/navigation";
import Link from "next/link";
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  CheckCircle2,
  CreditCard,
  Layers,
  Plus,
  Receipt,
  Search,
  Users,
  Wallet,
} from "lucide-react";
import {
  Badge,
} from "@/components/ui/badge";
import {
  Button,
} from "@/components/ui/button";
import {
  Input,
} from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Eyebrow,
  Panel,
} from "@/components/layout/app-shell";
import {
  PageHeader,
} from "@/components/layout/page-header";
import {
  EmptyState,
} from "@/components/state/empty-states";
import {
  TableSkeleton,
} from "@/components/state/skeletons";
import {
  useDebounced,
} from "@/components/certificates/certificates-table";
import {
  cn,
} from "@/lib/utils";

export type PlatformTenant = {
  id: string;
  name: string;
  slug: string;
  plan: string;
  users: number;
  certificates: number;
  contractDeployed: boolean;
  chainName: string | null;
  /** ISO date — formatted for display by the caller's locale helper. */
  expiresAtLabel: string;
  expiresSoon: boolean;
};

export type PlatformMetric = {
  label: string;
  value: string;
  /** "+3 this month" / "5 awaiting deploy" — tone drives the colour. */
  delta?: string;
  deltaTone?: "positive" | "warning" | "neutral";
};

export type OperatorAttentionItem = {
  id: string;
  kind: "allowance" | "seats" | "contract" | "expiry" | "gas";
  subject: string;
  title: string;
  meta: string;
  ctaLabel: string;
  href: string;
};

export type OperatorHomeData = {
  metrics: PlatformMetric[];
  attention: OperatorAttentionItem[];
  tenants: PlatformTenant[];
  newestTenants: { id: string; name: string; meta: string; href: string }[];
  newestCertificates: { id: string; name: string; meta: string; href: string }[];
  totals: { tenants: number; certificates: number; chains: number };
};

const ATTENTION_ICON = {
  allowance: CreditCard,
  seats: Users,
  contract: Layers,
  expiry: Receipt,
  gas: Wallet,
} as const;

const DELTA_CLASS = {
  positive: "text-success-foreground",
  warning: "text-warning-foreground",
  neutral: "text-muted-foreground",
} as const;

/** One strip, six compact metrics — not six large tiles. */
function MetricStrip({ metrics }: { metrics: PlatformMetric[] }) {
  return (
    <Panel className="grid grid-cols-2 gap-y-5 p-5 px-6 md:grid-cols-3 lg:grid-cols-6">
      {metrics.map((m) => (
        <div key={m.label} className="flex flex-col gap-0.5 border-l px-5 first:border-l-0 lg:border-l">
          <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {m.label}
          </span>
          <span className="whitespace-nowrap text-2xl font-semibold leading-tight tabular-nums">
            {m.value}
          </span>
          {m.delta && (
            <span
              className={cn(
                "whitespace-nowrap text-xs",
                DELTA_CLASS[m.deltaTone ?? "neutral"]
              )}
            >
              {m.delta}
            </span>
          )}
        </div>
      ))}
    </Panel>
  );
}

/** Healthy state: one reassuring line instead of an empty card. */
function NeedsAttention({
  items,
  totals,
}: {
  items: OperatorAttentionItem[];
  totals: OperatorHomeData["totals"];
}) {
  if (items.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-[var(--radius)] border border-success-border bg-success p-4 px-6">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-background">
          <CheckCircle2 className="size-[18px] text-success-foreground" />
        </span>
        <div className="flex flex-1 flex-col gap-0.5">
          <p className="text-sm font-semibold text-success-foreground">Nothing needs attention</p>
          <p className="text-[13px] text-success-foreground/90">
            All {totals.tenants} tenants are within allowance, every contract is deployed,
            and no subscription expires in the next 30 days.
          </p>
        </div>
        <Link
          href="/admin/audit"
          className="whitespace-nowrap text-[13px] font-medium text-primary hover:underline"
        >
          View audit log
        </Link>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[var(--radius)] border bg-card">
      <div className="flex items-center gap-2 border-b p-4 px-6">
        <h2 className="flex-1 text-xl font-semibold">Needs attention</h2>
        <Badge
          variant="outline"
          className="rounded-full border-accent-foreground/20 bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground"
        >
          {items.length}
        </Badge>
      </div>
      {items.map((a) => {
        const Icon = ATTENTION_ICON[a.kind];
        return (
          <div
            key={a.id}
            className="flex items-center gap-3 border-b px-6 py-3.5 last:border-b-0 hover:bg-muted/60"
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-accent">
              <Icon className="size-[17px] text-accent-foreground" />
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p className="text-sm text-pretty">
                <strong className="font-semibold">{a.subject}</strong> — {a.title}
              </p>
              <p className="text-xs text-muted-foreground">{a.meta}</p>
            </div>
            <Button variant="outline" size="sm" asChild className="shrink-0">
              <Link href={a.href}>{a.ctaLabel}</Link>
            </Button>
          </div>
        );
      })}
    </div>
  );
}

type TenantSortKey = "name" | "plan" | "users" | "certificates" | "expiresAtLabel";

function SortHead({
  label,
  k,
  sort,
  onSort,
  className,
  align = "left",
}: {
  label: string;
  k: TenantSortKey;
  sort: { key: TenantSortKey; dir: "asc" | "desc" };
  onSort: (k: TenantSortKey) => void;
  className?: string;
  align?: "left" | "right";
}) {
  const active = sort.key === k;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={cn(
          "flex w-full items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em]",
          align === "right" && "justify-end"
        )}
      >
        {label}
        <Icon className={cn("size-3", !active && "opacity-40")} />
      </button>
    </TableHead>
  );
}

function TenantsTable({
  tenants,
  isLoading,
}: {
  tenants: PlatformTenant[];
  isLoading?: boolean;
}) {
  const router = useRouter();

  const [qInput, setQInput] = React.useState("");
  const q = useDebounced(qInput).trim().toLowerCase();
  const [sort, setSort] = React.useState<{ key: TenantSortKey; dir: "asc" | "desc" }>({
    key: "certificates",
    dir: "desc",
  });

  const onSort = (k: TenantSortKey) =>
    setSort((s) =>
      s.key === k
        ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key: k, dir: k === "name" || k === "plan" ? "asc" : "desc" }
    );

  const rows = React.useMemo(() => {
    const filtered = q
      ? tenants.filter((t) =>
          `${t.name} ${t.slug} ${t.plan}`.toLowerCase().includes(q)
        )
      : tenants;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [tenants, q, sort]);

  return (
    <div className="overflow-hidden rounded-[var(--radius)] border bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b p-4 px-6">
        <h2 className="text-xl font-semibold">Tenants</h2>
        <span className="flex-1 text-[13px] tabular-nums text-muted-foreground">
          {q ? `${rows.length} match` : `${tenants.length} tenants`}
        </span>
        <div className="relative w-60">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search tenant or slug"
            aria-label="Search tenants"
            className="h-9 bg-background pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={8} showCheckbox={false} numericColumns={3} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Search}
          title="No tenants match"
          description="Try the slug instead of the display name."
        />
      ) : (
        <div className="w-full overflow-x-auto">
        <Table className="min-w-[720px] table-fixed">
          <TableHeader className="bg-muted">
            <TableRow className="hover:bg-transparent">
              <SortHead label="Tenant" k="name" sort={sort} onSort={onSort} className="h-9 pl-6" />
              <SortHead label="Plan" k="plan" sort={sort} onSort={onSort} className="w-[104px]" />
              <SortHead label="Users" k="users" sort={sort} onSort={onSort} className="w-[82px]" align="right" />
              <SortHead label="Certs" k="certificates" sort={sort} onSort={onSort} className="w-24" align="right" />
              <TableHead className="w-[136px] text-[11px] font-semibold uppercase tracking-[0.1em]">
                Contract
              </TableHead>
              <SortHead label="Expiry" k="expiresAtLabel" sort={sort} onSort={onSort} className="w-[126px] pr-6" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow
                key={t.id}
                className="h-12 cursor-pointer"
                onClick={() => router.push("/admin/tenants")}
              >
                  <TableCell className="pl-6">
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium">{t.name}</span>
                      <span className="truncate text-xs text-muted-foreground">{t.slug}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-[13px]">{t.plan}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{t.users}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {t.certificates.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        "gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium",
                        t.contractDeployed
                          ? "border-success-border bg-success text-success-foreground"
                          : "border-accent-foreground/20 bg-accent text-accent-foreground"
                      )}
                    >
                      <span className="size-1.5 rounded-full bg-current" aria-hidden />
                      {t.contractDeployed ? (t.chainName ?? "Deployed") : "Not deployed"}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className={cn(
                      "whitespace-nowrap pr-6 text-[13px]",
                      t.expiresSoon ? "text-accent-foreground" : "text-muted-foreground"
                    )}
                  >
                    {t.expiresAtLabel}
                  </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      )}
    </div>
  );
}

function ActivityList({
  title,
  items,
  allHref,
}: {
  title: string;
  items: { id: string; name: string; meta: string; href: string }[];
  allHref: string;
}) {
  return (
    <div className="overflow-hidden rounded-[var(--radius)] border bg-card">
      <div className="flex items-baseline gap-2 border-b p-4 px-6">
        <Eyebrow>{title}</Eyebrow>
        <div className="flex-1" />
        <Link href={allHref} className="text-[13px] font-medium text-primary hover:underline">
          All
        </Link>
      </div>
      {items.map((i) => (
        <Link
          key={i.id}
          href={i.href}
          className="flex flex-col gap-0.5 border-b px-6 py-3 last:border-b-0 hover:bg-muted/60"
        >
          <span className="truncate text-sm font-medium">{i.name}</span>
          <span className="text-xs text-muted-foreground">{i.meta}</span>
        </Link>
      ))}
    </div>
  );
}

export function OperatorHome({
  data,
  isLoading,
}: {
  data: OperatorHomeData;
  isLoading?: boolean;
}) {
  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Overview"
        description={`${data.totals.tenants} tenants · ${data.totals.certificates.toLocaleString()} certificates issued across ${data.totals.chains} chains.`}
        help={
          <div className="flex flex-col gap-2">
            <Eyebrow>What lands here</Eyebrow>
            <p className="text-[13px] leading-relaxed text-foreground/80">
              Anything a tenant can’t fix themselves: allowance overages, undeployed
              contracts, expiring subscriptions and low gas wallets.
            </p>
          </div>
        }
        secondaryActions={
          <Button variant="outline" asChild>
            <Link href="/admin/audit">Audit log</Link>
          </Button>
        }
        primaryAction={
          <Button className="gap-2" asChild>
            <Link href="/admin/tenants?tab=create">
              <Plus className="size-4" />
              Create tenant
            </Link>
          </Button>
        }
      />

      <MetricStrip metrics={data.metrics} />

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_340px]">
        <div className="flex min-w-0 flex-col gap-6">
          <NeedsAttention items={data.attention} totals={data.totals} />
          <TenantsTable tenants={data.tenants} isLoading={isLoading} />
        </div>
        <div className="flex flex-col gap-6">
          <ActivityList
            title="Newest tenants"
            items={data.newestTenants}
            allHref="/admin/tenants"
          />
          <ActivityList
            title="Newest certificates"
            items={data.newestCertificates}
            allHref="/certificates"
          />
        </div>
      </div>
    </>
  );
}
